<template>
    <div>
        <div class="d-flex justify-content-even position-relative" style="width: 115px; height: 8px">
            <div
                class="h-100 rounded d-flex flex-grow-1"
                style="margin: 0 1px; transition: background 250ms cubic-bezier(0, 0, 0, 0.76)"
                :class="{
                    'bg-secondary': isPasswordEmpty,
                    'bg-warning': isPasswordMedium,
                    'bg-danger': isPasswordWeak,
                    'bg-success': isPasswordStrong,
                }"
            ></div>
            <div
                class="h-100 rounded d-flex flex-grow-1"
                style="margin: 0 1px; transition: background 250ms cubic-bezier(0, 0, 0, 0.76)"
                :class="{
                    'bg-secondary': isPasswordEmpty || isPasswordWeak,
                    'bg-warning': isPasswordMedium,
                    'bg-success': isPasswordStrong,
                }"
            ></div>
            <div
                class="h-100 rounded d-flex flex-grow-1"
                style="margin: 0 1px; transition: background 250ms cubic-bezier(0, 0, 0, 0.76)"
                :class="{ 'bg-secondary': !isPasswordStrong, 'bg-success': isPasswordStrong }"
            ></div>

            <div
                v-if="!isPasswordEmpty"
                class="font-size-75 position-absolute w-100 text-center font-weight-bold"
                style="top: 100%"
                :class="{
                    'text-danger': isPasswordWeak,
                    'text-warning': isPasswordMedium,
                    'text-success': isPasswordStrong,
                }"
            >
                {{ passwordStrengthHint }}
            </div>
        </div>
    </div>
</template>

<script>
import { testIsString, testIsEmptyString } from '../../../../../lib/test-and-assert/test-base';
import { translate } from '../../../../helper/translate';

// https://iocane.com.au/talking-passwords-and-entropy/
// < 28 bits = Very Weak; might keep out family members
// 28 – 35 bits = Weak; should keep out most people, often good for desktop login passwords.
// 36 – 59 bits = Reasonable; fairly secure passwords for network and company passwords.
// 60 – 127 bits = Strong; can be good for guarding financial information.
// 128+ bits = Very Strong; often overkill

export default {
    name: 'PasswordStrengthMeter',
    props: {
        password: {
            type: String,
            required: true,
        },
    },
    computed: {
        passwordStrengthHint() {
            if (this.isPasswordWeak) {
                return translate('weak');
            }
            if (this.isPasswordMedium) {
                return translate('average');
            }
            if (this.isPasswordStrong) {
                return translate('strong');
            }
        },
        passwordEntropy() {
            if (this.isPasswordEmpty) {
                return null;
            }

            // this calculates the shannon entropy of the password
            // code shamelessly stolen from https://gist.github.com/ppseprus/afab8500dec6394c401734cb6922d220
            // (and slightly modified)

            // create array of character frequencies by counting how often
            // each individual character occurs in the password
            const characterFrequencies = [...new Set(this.password)].map((chr) => {
                const escapedRegex = chr.replace(/([.?*+^$\[\]\\(){}|-])/g, '\\$1');

                return this.password.match(new RegExp(escapedRegex, 'g')).length;
            });

            // calculate entropy of a string in bits per symbol
            const entropyPerSymbol = characterFrequencies.reduce((sum, frequency) => {
                const p = frequency / this.password.length;

                return sum + p * Math.log2(1 / p);
            }, 0);

            // calculate total entropy of string in bits
            return entropyPerSymbol * this.password.length;
        },
        isPasswordEmpty() {
            return !testIsString(this.password) || testIsEmptyString(this.password);
        },
        isPasswordWeak() {
            if (this.isPasswordEmpty) {
                return false;
            }

            return this.passwordEntropy <= this.weakEntropy;
        },
        isPasswordMedium() {
            if (this.isPasswordEmpty) {
                return false;
            }

            return this.passwordEntropy > this.weakEntropy && this.passwordEntropy <= this.mediumEntropy;
        },
        isPasswordStrong() {
            if (this.isPasswordEmpty) {
                return false;
            }

            return this.passwordEntropy > this.mediumEntropy;
        },
    },
    data() {
        return {
            // TODO find out if these values feel okay for users and adjust accordingly
            weakEntropy: 35,
            mediumEntropy: 59,
        };
    },
};
</script>
